import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import PageShell from "../../components/layout/PageShell";
import {
    getClientCrmSettings,
    saveClientCrmSettings
} from "../../services/crmService";

import "../Setting.css";

function SettingHelp({ title, children }) {
    return (
        <div className="client-setting-help">
            <strong>{title}</strong>
            <span>{children}</span>
        </div>
    );
}

function ToggleSetting({ checked, onChange, title, description }) {
    return (
        <label className="client-toggle-card">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
            />
            <div>
                <strong>{title}</strong>
                <span>{description}</span>
            </div>
        </label>
    );
}

export default function ClientCrmSettingsPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        showPublishedModulesOnWebsite: true,
        allowItemLevelAiImprovement: true,
        allowImageSuggestions: true,

        defaultLeadOwner: "",
        notificationEmail: "",
        leadStatuses: "New, Contacted, Won, Lost",

        notifyOnNewLead: true,
        autoAssignLeads: false,
        allowTeamManagement: false
    });

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            try {
                setLoading(true);

                const data = await getClientCrmSettings();

                if (!mounted) return;

                setForm(prev => ({
                    ...prev,
                    ...(data || {})
                }));
            } catch (error) {
                console.error(error);
                toast.error("Failed to load CRM settings.");
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, []);

    const update = (key, value) => {
        setForm(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            toast.loading("Saving CRM settings...", { id: "client-crm-settings" });

            const saved = await saveClientCrmSettings(form);

            setForm(prev => ({
                ...prev,
                ...(saved || {})
            }));

            toast.success("CRM settings saved.", { id: "client-crm-settings" });
        } catch (error) {
            console.error(error);
            toast.error(
                error?.response?.data || "Failed to save CRM settings.",
                { id: "client-crm-settings" }
            );
        } finally {
            setSaving(false);
        }
    };

    const statuses = useMemo(() => {
        return String(form.leadStatuses || "")
            .split(",")
            .map(x => x.trim())
            .filter(Boolean);
    }, [form.leadStatuses]);

    if (loading) {
        return (
            <PageShell title="CRM & Leads Settings">
                <div className="project-loading-card">Loading CRM settings...</div>
            </PageShell>
        );
    }

    return (
        <PageShell title="CRM & Leads Settings">
            <div className="settings-shell client-settings-shell">
                <section className="client-settings-hero">
                    <div>
                        <span className="project-kicker">CRM Settings</span>
                        <h1>CRM, Leads & AI Assistance</h1>
                        <p>
                            Decide how website content is managed, how new enquiries are handled, and which AI helper actions are available.
                        </p>

                        <div className="client-settings-summary">
                            <span>{statuses.length || 0} lead statuses</span>
                            <span>{form.notifyOnNewLead ? "Lead notifications on" : "Lead notifications off"}</span>
                            <span>{form.allowItemLevelAiImprovement ? "AI editing on" : "AI editing off"}</span>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="settings-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Settings"}
                    </button>
                </section>

                <section className="client-settings-layout">
                    <div className="client-settings-main">
                        <div className="project-panel client-setting-panel">
                            <span className="project-kicker">Website content control</span>
                            <h2>Content & AI Editing</h2>
                            <p className="settings-muted">
                                These options control what editors can change and whether AI helper tools appear inside module editors.
                            </p>

                            <div className="client-toggle-grid">
                                <ToggleSetting
                                    checked={!!form.showPublishedModulesOnWebsite}
                                    onChange={(value) => update("showPublishedModulesOnWebsite", value)}
                                    title="Show published content on website"
                                    description="Published CRM items such as programs, products, listings, and testimonials can appear on the live website."
                                />

                                <ToggleSetting
                                    checked={!!form.allowItemLevelAiImprovement}
                                    onChange={(value) => update("allowItemLevelAiImprovement", value)}
                                    title="Allow AI to improve individual items"
                                    description="Editors can ask AI to rewrite or improve one program, product, listing, or testimonial."
                                />

                                <ToggleSetting
                                    checked={!!form.allowImageSuggestions}
                                    onChange={(value) => update("allowImageSuggestions", value)}
                                    title="Allow AI image suggestions"
                                    description="Editors can request image suggestions for CRM items, such as program cards or product cards."
                                />
                            </div>
                        </div>

                        <div className="project-panel client-setting-panel">
                            <span className="project-kicker">Lead handling</span>
                            <h2>New Enquiry Workflow</h2>
                            <p className="settings-muted">
                                These settings decide who should handle new leads and what status options your team sees.
                            </p>

                            <div className="settings-field-grid">
                                <label>
                                    <span>Default Lead Owner</span>
                                    <input
                                        value={form.defaultLeadOwner || ""}
                                        onChange={(e) => update("defaultLeadOwner", e.target.value)}
                                        placeholder="Example: Sales Team, Kishan, Front Desk"
                                    />
                                    <small>Used when auto-assign is enabled. This appears as the owner of new leads.</small>
                                </label>

                                <label>
                                    <span>Lead Notification Email</span>
                                    <input
                                        value={form.notificationEmail || ""}
                                        onChange={(e) => update("notificationEmail", e.target.value)}
                                        placeholder="owner@example.com"
                                    />
                                    <small>Used for future lead email alerts and internal follow-up visibility.</small>
                                </label>

                                <label className="settings-wide-field">
                                    <span>Lead Status Options</span>
                                    <input
                                        value={form.leadStatuses || ""}
                                        onChange={(e) => update("leadStatuses", e.target.value)}
                                        placeholder="New, Contacted, Won, Lost"
                                    />
                                    <small>Comma-separated list shown in the lead pipeline.</small>
                                </label>
                            </div>

                            <div className="client-status-preview">
                                {statuses.length === 0 ? (
                                    <span>No statuses configured</span>
                                ) : (
                                    statuses.map(item => (
                                        <span key={item}>{item}</span>
                                    ))
                                )}
                            </div>

                            <div className="client-toggle-grid two">
                                <ToggleSetting
                                    checked={!!form.notifyOnNewLead}
                                    onChange={(value) => update("notifyOnNewLead", value)}
                                    title="Notify team when a new lead arrives"
                                    description="New website enquiries can be marked for notification using the email above."
                                />

                                <ToggleSetting
                                    checked={!!form.autoAssignLeads}
                                    onChange={(value) => update("autoAssignLeads", value)}
                                    title="Auto-assign new leads"
                                    description="New leads will automatically receive the default owner name."
                                />
                            </div>
                        </div>

                        <div className="project-panel client-setting-panel">
                            <span className="project-kicker">Team access</span>
                            <h2>Team Management</h2>
                            <p className="settings-muted">
                                Team roles will allow owners, editors, and viewers later. Keep this off unless your workspace is ready for multiple users.
                            </p>

                            <div className="client-toggle-grid">
                                <ToggleSetting
                                    checked={!!form.allowTeamManagement}
                                    onChange={(value) => update("allowTeamManagement", value)}
                                    title="Prepare this account for team access"
                                    description="Enables the account-level setting for future team management screens."
                                />
                            </div>

                            <button type="button" className="project-action-btn primary" disabled>
                                Team invitations coming soon
                            </button>
                        </div>
                    </div>

                    <aside className="client-settings-side">
                        <SettingHelp title="What does this page affect?">
                            It affects CRM editing behavior, AI helper buttons, lead assignment, and lead status options.
                        </SettingHelp>

                        <SettingHelp title="Will this change my website immediately?">
                            Not directly. These settings control what appears in the CRM and how future leads/content actions behave.
                        </SettingHelp>

                        <SettingHelp title="Recommended setup">
                            Keep AI improvement and image suggestions on, set a default lead owner, and keep lead statuses simple for beta.
                        </SettingHelp>
                    </aside>
                </section>

                <div className="settings-sticky-save">
                    <span>CRM, Leads & AI settings</span>

                    <button
                        type="button"
                        className="settings-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Settings"}
                    </button>
                </div>
            </div>
        </PageShell>
    );
}